import { SpotMarketConfig } from '@drift-labs/sdk';
import { MAIN_POOL_ID } from '@drift/common';
import { useState } from 'react';
import { CollateralModalProps } from 'src/components/Modals/CollateralModal/types';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';

export default function useCollateralModalState({
	collateralIndex,
	collateralPoolId,
	targetAccountKey,
	defaultLargestBalance,
}: CollateralModalProps) {
	const spotMarketsLookup = useSpotMarketsLookup();
	const spotMarkets = useSpotMarkets();
	const [selectedTargetAccountKey, setSelectedTargetAccountKey] =
		useState<string>(targetAccountKey ?? '');
	const [selectedCollateralMarket, setSelectedCollateralMarket] =
		useState<SpotMarketConfig>(spotMarketsLookup[collateralIndex ?? 0]);
	const selectedTargetAccount = useDriftAccountStore(
		(s) => s.accounts[selectedTargetAccountKey ?? '']
	);
	const [selectedPoolId, setSelectedPoolId] = useState<number>(
		collateralPoolId ??
			selectedCollateralMarket.poolId ??
			selectedTargetAccount?.poolId ??
			MAIN_POOL_ID
	);
	const getAccountStoreState = useDriftAccountStore((s) => s.get);

	const handleTargetAccountChange = (newAccountKey: string) => {
		const newTargetUserAccount = getAccountStoreState().accounts[newAccountKey];

		if (!selectedTargetAccount || !newTargetUserAccount) {
			console.error(
				'No selected target account or new target user account found'
			);
			return;
		}

		const poolIdMatches =
			selectedTargetAccount.poolId === newTargetUserAccount.poolId;

		const poolIdToUse = newTargetUserAccount.poolId;

		const newSelectedMarket = poolIdMatches
			? selectedCollateralMarket
			: spotMarkets.find(
					(market) => market.poolId === newTargetUserAccount.poolId
				);

		if (!newSelectedMarket) {
			console.error('No new selected market found');
			return;
		}

		setSelectedTargetAccountKey(newAccountKey);
		setSelectedPoolId(poolIdToUse);
		setSelectedCollateralMarket(newSelectedMarket);
	};

	const updateCollateralIndex = (newMarketIndex: number) => {
		const newMarket = spotMarkets.find(
			(market) => market.marketIndex === newMarketIndex
		);

		if (!newMarket) {
			console.error('No new market found');
			return;
		}

		setSelectedCollateralMarket(newMarket);
	};

	return {
		selectedCollateralMarket,
		selectedTargetAccountKey,
		selectedPoolId,
		selectedTargetAccount,
		defaultToLargestBalance: !!defaultLargestBalance,
		handleTargetAccountChange,
		updateCollateralIndex,
	};
}
